import { Fragment, useState } from 'react'
import {
  Badge,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { CheckIcon, CloseIcon } from '@chakra-ui/icons'

import { CustomDivider, FormItem } from 'components'
import { CustomFormAnswer, CustomFormVerificationDataForm } from 'components/tools/CustomForm/types'
import { useCustomFormAnswer } from 'components/tools/CustomForm/components/Answers/contexts/CustomFormViewerQuestionManager/Provider'
import {
  VALIDATE_QUESTION_ANSWER_STATUS_OBJECT,
  VALIDATE_QUESTION_ANSWER_STATUS_OPTIONS,
} from 'components/tools/CustomForm/constants'
import CustomFormValidateDataAlert from './components/CustomFormValidateDataAlert'

type Props = {
  answer: CustomFormAnswer
}

export default function CustomFormValidateUploadDataModal({ answer }: Props) {
  const [loadingVerification, setLoadingverification] = useState(false)
  const [answerVerifications, setAnswerVerifications] = useState<CustomFormAnswer>(answer)

  const { validationAnswer } = useCustomFormAnswer()

  const validateAnswerStatus =
    VALIDATE_QUESTION_ANSWER_STATUS_OBJECT[answerVerifications.answer_reviewed_status as string]

  const validateAnswerIcon = validateAnswerStatus?.color === 'green' ? <CheckIcon /> : <CloseIcon boxSize={2} />

  const formMethods = useForm<CustomFormVerificationDataForm>({
    defaultValues: { reviewed_status: '', reviewed_description: '' },
  })

  const { isOpen, onOpen, onClose } = useDisclosure()

  async function onSubmit(formValues: CustomFormVerificationDataForm) {
    if (!validationAnswer) return
    setLoadingverification(true)
    const response = await validationAnswer({
      questionId: answer.id,
      data: formValues,
    })
    if (response?.header?.success) {
      const { reviewed_by, reviewed_status, reviewed_description, reviewed_at } = response?.body?.form_answer
      setAnswerVerifications({
        ...answerVerifications,
        answer_reviewed_status: reviewed_status,
        answer_reviewed_description: reviewed_description,
        answer_reviewed_at: reviewed_at,
        answer_reviewed_by: {
          reviewed_by_name: reviewed_by.reviewed_by_name,
          reviewed_by_avatar_id: reviewed_by.reviewed_by_avatar_id,
          reviewed_by_avatar_url: reviewed_by.reviewed_by_avatar_url,
          reviewed_by_avatar_extension: reviewed_by.reviewed_by_avatar_extension,
          reviewed_by_avatar_size: reviewed_by.reviewed_by_avatar_size,
          reviewed_by_avatar_name: reviewed_by.reviewed_by_avatar_name,
        },
      })
    }
    onClose()
    formMethods.reset({})
    setLoadingverification(false)
  }

  return (
    <>
      {validationAnswer && (
        <Fragment>
          {validateAnswerStatus?.color ? (
            <Badge
              variant="solid"
              size="xs"
              colorScheme={validateAnswerStatus?.color}
              onClick={onOpen}
              className="accessible"
            >
              <Flex align="center" justify="center" gap={1}>
                {validateAnswerIcon}
                Validado
              </Flex>
            </Badge>
          ) : (
            <Badge variant="solid" size="xs" colorScheme="gray" onClick={onOpen} className="accessible">
              Não validado
            </Badge>
          )}
        </Fragment>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>
            <Text>Validando a resposta da pergunta:</Text>
            <Heading color="primary">{answer.title}</Heading>
          </ModalHeader>

          <ModalBody>
            <Stack as="form" id="custom-form-verification-data-form" onSubmit={formMethods.handleSubmit(onSubmit)}>
              {answerVerifications?.answer_reviewed_at && (
                <Fragment>
                  <CustomFormValidateDataAlert verification={answerVerifications} />

                  <CustomDivider />
                </Fragment>
              )}

              <FormItem label="Resultado da validação">
                <RadioGroup size="md">
                  <Flex align="center" gap={4}>
                    {VALIDATE_QUESTION_ANSWER_STATUS_OPTIONS.map((option) => (
                      <Flex key={option.value} align="center" gap={2}>
                        <Radio value={option.value} {...formMethods.register('reviewed_status')} />
                        <Text>{option.label}</Text>
                      </Flex>
                    ))}
                  </Flex>
                </RadioGroup>
              </FormItem>

              <FormItem label="Observação">
                <Textarea {...formMethods.register('reviewed_description')} />
              </FormItem>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
            <Button isLoading={loadingVerification} type="submit" form="custom-form-verification-data-form">
              Verificar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
